import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { listenToHostPayout } from "../../redux/payoutsSlice";
import { motion } from "framer-motion";
import { Users } from "lucide-react";
import { CircularProgress } from "@mui/material";

// 🔹 Helper: format large numbers (1.2K, 3.4M, etc.)
const formatNumber = (num: number = 0): string => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

const Referrals: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { referrals, loading } = useSelector(
    (state: RootState) => state.payouts
  );
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (user?.userId) dispatch(listenToHostPayout(user.userId));
  }, [dispatch, user]);

  if (loading)
    return (
      <div style={styles.loader}>
        <CircularProgress sx={{ color: "#bf9b30" }} />
      </div>
    );

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Your Referrals</h2>

      <motion.div
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 180 }}
        style={styles.card}
      >
        <div style={styles.iconBox}>
          <Users size={28} />
        </div>
        <div>
          <h3 style={styles.value}>
            <span style={styles.euroSymbol}>€</span>
            {formatNumber(referrals)}
          </h3>
          <p style={styles.label}>Total Referrals</p>
        </div>
      </motion.div>
    </div>
  );
};

export default Referrals;

// ─── Styles ─────────────────────────────────────────────
const styles: { [k: string]: React.CSSProperties } = {
  container: {
    color: "#fff",
    padding: "24px",
    fontFamily: "'Poppins', sans-serif",
    textAlign: "center",
  },
  header: {
    color: "#bf9b30",
    textTransform: "uppercase",
    marginBottom: 24,
    letterSpacing: 1,
    fontWeight: 600,
  },
  loader: {
    height: "50vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.4)",
    borderRadius: 18,
    padding: "30px 40px",
    display: "inline-flex",
    alignItems: "center",
    gap: 18,
    boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
    cursor: "default",
  },
  iconBox: {
    background: "rgba(191,155,48,0.15)",
    borderRadius: "50%",
    width: 60,
    height: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bf9b30",
  },
  value: {
    fontSize: 32,
    fontWeight: 800,
    color: "#fff",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  euroSymbol: {
    fontSize: 28,
    fontWeight: 800,
    color: "#bf9b30",
    marginRight: 4,
  },
  label: {
    fontSize: 14,
    color: "#bbb",
    margin: 0,
  },
};
